import type { ProductVariantColor_ } from 'src/api/db/apiProductVariantColor'

type ProductImagePreview = {
  image_url?: string | null
  order?: number | null
}

export type QuoteItemThumbnailSource = {
  product_variant_color_id?: number | null
  /** Flat list-query join via quote_item.product_variant_color_id */
  product_variant_color?: {
    id?: number
    product_image?: ProductImagePreview[]
  } | null
  product?: {
    product_variant?: Array<{
      product_variant_color?: Array<{
        id?: number
        color?: string | null
        product_image?: ProductImagePreview[]
      }>
    }>
  } | null
}

export function getVariantColorImageUrl(
  color?: {
    product_image?: Array<{
      image_url?: string | null
      order?: number | null
    }>
  } | null
): string | null {
  const images = [...(color?.product_image ?? [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  )
  return images[0]?.image_url ?? null
}

/** Resolves the stored color and its sibling swatches across all product variants. */
export function resolveQuoteItemVariantColor(
  item: QuoteItemThumbnailSource
): {
  chosen_product_variant_color: ProductVariantColor_ | undefined
  product_variant_colors: ProductVariantColor_[]
} {
  const colorId = item.product_variant_color_id
  const variants = item.product?.product_variant ?? []

  if (colorId) {
    for (const variant of variants) {
      const colors = (variant.product_variant_color ??
        []) as ProductVariantColor_[]
      const chosen = colors.find((color) => color.id === colorId)
      if (chosen) {
        return {
          chosen_product_variant_color: chosen,
          product_variant_colors: colors,
        }
      }
    }
  }

  const fallbackColors = (variants[0]?.product_variant_color ??
    []) as ProductVariantColor_[]
  const chosen =
    colorId != null
      ? (fallbackColors.find((color) => color.id === colorId) ??
        fallbackColors[0])
      : fallbackColors[0]

  return {
    chosen_product_variant_color: chosen,
    product_variant_colors: fallbackColors,
  }
}

export function getQuoteItemThumbnailUrl(
  item: QuoteItemThumbnailSource
): string | null {
  const directUrl = getVariantColorImageUrl(item.product_variant_color)
  if (directUrl) return directUrl

  const product = item.product
  const pvcId = item.product_variant_color_id
  const variants = product?.product_variant
  if (!variants?.length) return null

  for (const pv of variants) {
    const colors = pv.product_variant_color
    if (!colors?.length) continue

    const color = pvcId
      ? (colors.find((c) => c.id === pvcId) ?? colors[0])
      : colors[0]

    const url = getVariantColorImageUrl(color)
    if (url) return url
  }

  return null
}

export function getQuotePreviewThumbnails<
  T extends QuoteItemThumbnailSource & { id?: number },
>(items: T[] | undefined, limit = 4) {
  const previews: { key: string; url: string | null }[] = []

  for (const [index, item] of (items ?? []).entries()) {
    if (previews.length >= limit) break
    previews.push({
      key: String(item.id ?? index),
      url: getQuoteItemThumbnailUrl(item),
    })
  }

  return {
    previews,
    extraCount: Math.max(0, (items?.length ?? 0) - previews.length),
  }
}
